import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import RoleCapabilityBadge from '../../../components/RoleCapabilityBadge'
import { useRoleListScreen } from './hooks/useRoleListScreen'

function statusPillClasses(status) {
  if (status === 'Active') return 'bg-emerald-50 text-emerald-700 border-emerald-100'
  if (status === 'Pending Review' || status === 'Pending') return 'bg-amber-50 text-amber-700 border-amber-200'
  if (status === 'Rejected') return 'bg-red-50 text-red-700 border-red-200'
  return 'bg-surface-container-high text-outline border-outline-variant/30'
}

export default function RoleRightsListScreen() {
  const { state, actions } = useRoleListScreen()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8">
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0 max-w-xl">
          <h1 className="text-4xl font-extrabold tracking-[-0.02em] text-primary">Role Matrix</h1>
          <p className="mt-2 text-base text-on-surface-variant">
            Govern enterprise access across your organizational architecture with granular control.
          </p>
        </div>

        <div className="flex flex-nowrap items-end gap-2.5 sm:gap-3 rounded-2xl bg-surface-container-low p-2.5 sm:p-3 shrink-0">
          <label className="grid min-w-[140px] sm:min-w-[160px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Role Name</span>
            <input
              type="text"
              value={state.roleName}
              onChange={(event) => actions.setRoleName(event.target.value)}
              placeholder="Search role name..."
              className="rounded-full border-0 bg-surface-container-lowest px-4 py-2 text-sm font-semibold text-on-surface shadow-none outline-none placeholder:font-normal placeholder:text-on-surface-variant/60"
            />
          </label>

          <label className="grid min-w-[110px] sm:min-w-[130px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Role Code</span>
            <input
              type="text"
              value={state.roleCode}
              onChange={(event) => actions.setRoleCode(event.target.value)}
              placeholder="Search code..."
              className="rounded-full border-0 bg-surface-container-lowest px-4 py-2 text-sm font-semibold text-on-surface shadow-none outline-none placeholder:font-normal placeholder:text-on-surface-variant/60"
            />
          </label>

          <label className="grid min-w-[130px] sm:min-w-[150px] gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Status</span>
            <AppDropdown
              value={state.statusFilter}
              onChange={actions.setStatusFilter}
              triggerClassName="rounded-full border-0 bg-surface-container-lowest px-4 py-2 text-sm font-semibold text-on-surface shadow-none"
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </label>

          {state.hasActiveFilters && (
            <button
              type="button"
              onClick={actions.resetFilters}
              className="rounded-full border-0 bg-surface-container-lowest px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-red-600 transition-colors hover:bg-red-50 shadow-none shrink-0 whitespace-nowrap"
            >
              Reset
            </button>
          )}
        </div>
      </section>

      {state.error ? (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="space-y-3">
        <div className="grid grid-cols-12 rounded-t-xl bg-surface-container/30 px-8 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
          <div className="col-span-4">Role &amp; Identity</div>
          <div className="col-span-2 text-center">Code</div>
          <div className="col-span-2 text-center">Data Scope</div>
          <div className="col-span-2 text-center">Assigned Users</div>
          <div className="col-span-2 text-right">Status</div>
        </div>

        {state.isLoading ? (
          <div className="rounded-xl bg-surface-container-lowest px-6 py-12 text-center text-sm text-on-surface-variant">
            Loading roles...
          </div>
        ) : state.rows.length === 0 ? (
          <div className="rounded-xl bg-surface-container-lowest px-6 py-12 text-center text-sm text-on-surface-variant">
            No roles found for selected filters.
          </div>
        ) : (
          state.rows.map((row) => {
            const RowTag = row.status === 'Active' ? Link : 'div'
            const rowProps =
              row.status === 'Active'
                ? { to: `/masters/roles/entry?id=${row.id}` }
                : { title: 'Archived role is read-only.' }

            return (
              <RowTag
                key={row.id}
                {...rowProps}
                className={`grid grid-cols-12 items-center rounded-xl bg-surface-container-lowest px-8 py-6 transition-all duration-200 ${row.status === 'Active'
                    ? 'hover:bg-surface-container-low'
                    : 'cursor-not-allowed opacity-90'
                  }`}
              >
                <div className="col-span-4 flex items-center gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-xl font-bold leading-tight text-on-surface">{row.name}</h3>
                      <RoleCapabilityBadge capabilityKey={row.capability} />
                    </div>
                    <p className="mt-0.5 text-sm text-on-surface-variant">{row.description}</p>
                  </div>
                </div>

                <div className="col-span-2 text-center">
                  <span className="rounded-md bg-surface-container-high px-3 py-1 text-xs font-mono text-on-surface-variant">
                    {row.code}
                  </span>
                </div>

                <div className="col-span-2 text-center">
                  <span className="rounded-full bg-surface-container-high px-3 py-1 text-xs font-bold text-on-surface">
                    {row.dataScope}
                  </span>
                </div>

                <div className="col-span-2 text-center">
                  <p className="text-3xl font-black tracking-tighter text-primary">{row.assignedUsers}</p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-on-surface-variant/70">Active Users</p>
                </div>

                <div className="col-span-2 flex justify-end">
                  <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-bold ${statusPillClasses(row.status)}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${row.status === 'Active' ? 'bg-emerald-500' : (row.status === 'Pending Review' || row.status === 'Pending' ? 'bg-amber-500' : row.status === 'Rejected' ? 'bg-red-500' : 'bg-outline')}`} />
                    {row.status.toUpperCase()}
                  </span>
                </div>
              </RowTag>
            )
          })
        )}
      </section>

      <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-3 py-5 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm text-on-surface-variant">
          Showing {state.rangeStart} - {state.rangeEnd} of {state.totalCount} roles
        </span>

        <AppPagination
          page={state.page}
          totalPages={state.totalPages}
          onPageChange={actions.goToPage}
          onPrevious={actions.goToPreviousPage}
          onNext={actions.goToNextPage}
          canPrevious={state.page > 1}
          canNext={Boolean(state.hasMore) || state.page < state.totalPages}
        />
      </div>

      <section className="grid grid-cols-1 gap-6 border-t border-outline-variant/10 pt-6 md:grid-cols-3">
        <article className="rounded-2xl bg-surface-container-low p-6">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Total Users Bound</span>
            <span className="material-symbols-outlined text-primary">groups</span>
          </div>
          <p className="mt-3 text-4xl font-black tracking-tighter text-on-surface">{state.stats.totalUsersBound}</p>
          <div className="mt-4 h-2 w-full rounded-full bg-surface-variant">
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(8, Math.min(100, state.stats.activeShare))}%` }} />
          </div>
        </article>

        <article className="rounded-2xl bg-surface-container-low p-6">
          {/* <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">System Capacity</span>
            <span className="material-symbols-outlined text-primary">storage</span>
          </div>
          <p className="mt-3 text-4xl font-black tracking-tighter text-on-surface">{state.stats.activeShare}%</p>
          <div className="mt-4 h-2 w-full rounded-full bg-surface-variant">
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(8, state.stats.activeShare)}%` }} />
          </div> */}
        </article>

        <article className="rounded-2xl bg-primary-container p-6 text-on-primary">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-[0.14em] text-white/75">Security Pulse</span>
            <span className="material-symbols-outlined">security</span>
          </div>
          <p className="mt-3 text-base leading-relaxed">
            Average rights assignment in this view is {state.stats.averageRights}. Review elevated-access roles
            periodically.
          </p>
          <button type="button" className="mt-4 w-full rounded-md bg-white/15 py-2 text-xs font-black uppercase tracking-[0.14em] hover:bg-white/25">
            Run Security Audit
          </button>
        </article>
      </section>
    </div>
  )
}


